import { useEffect, useState } from 'react'
import { TypedEmitter } from 'tiny-typed-emitter'

type CollapsibleGroup = {
    allOpen: boolean
    allClosed: boolean
    openAll(): void
    closeAll(): void
}

// Use this hook in the place that has controls to open/close all items in a group.
export function useCollapsibleGroup(groupId: string): CollapsibleGroup {
    const [allOpen, setAllOpen] = useState(manager.areAllOpen(groupId))
    const [allClosed, setAllClosed] = useState(manager.areAllClosed(groupId))

    useEffect(() => {
        const h = (_: boolean, gid: string) => {
            if (gid === groupId) {
                setAllOpen(manager.areAllOpen(groupId))
                setAllClosed(manager.areAllClosed(groupId))
            }
        }
        manager.on('change', h)
        return () => {
            manager.off('change', h)
        }
    }, [])

    return {
        allOpen,
        allClosed,
        openAll: () => manager.openAll(groupId),
        closeAll: () => manager.closeAll(groupId),
    }
}

// Use this hook to manage the state of a single collapsible item in a group.
export function useCollapsibleGroupItem(groupId: string, itemId: string): [boolean, (open: boolean) => void] {
    const [open, setOpen] = useState(manager.getItemState(groupId, itemId))
    useEffect(() => {
        const h = (nextOpen: boolean, gid: string, id: string) => {
            if (gid === groupId && (id === itemId || id === '*')) {
                setOpen(nextOpen)
            }
        }
        manager.on('change', h)
        return () => {
            manager.off('change', h)
        }
    }, [itemId])

    return [open, (v: boolean) => manager.setOpen(v, groupId, itemId)]
}

type CollapsibleGroupEventEmitter = {
    change: (open: boolean, groupId: string, itemId: string) => void
}
class CollapsibleGroupManager extends TypedEmitter<CollapsibleGroupEventEmitter> {
    public states: Map<string, Map<string, boolean>> = new Map()

    public setOpen(open: boolean, groupId: string, itemId: string) {
        const groupState = this.getGroupState(groupId)
        groupState.set(itemId, open)
        this.emit('change', open, groupId, itemId)
    }

    public openAll(groupId: string) {
        const groupState = this.getGroupState(groupId)
        groupState.forEach((_, itemId) => {
            groupState.set(itemId, true)
        })
        this.emit('change', true, groupId, '*')
    }

    public closeAll(groupId: string) {
        const groupState = this.getGroupState(groupId)
        groupState.forEach((_, itemId) => {
            groupState.set(itemId, false)
        })
        this.emit('change', false, groupId, '*')
    }

    public areAllOpen(groupId: string): boolean {
        const groupState = this.getGroupState(groupId)
        return Array.from(groupState.values()).every((v) => v)
    }

    public areAllClosed(groupId: string): boolean {
        const groupState = this.getGroupState(groupId)
        return Array.from(groupState.values()).every((v) => !v)
    }

    public getGroupState(groupId: string): Map<string, boolean> {
        if (!this.states.has(groupId)) {
            this.states.set(groupId, new Map())
        }
        return this.states.get(groupId)!
    }

    public getItemState(groupId: string, itemId: string, defaultOpen: boolean = true): boolean {
        const groupState = this.getGroupState(groupId)
        if (!groupState.has(itemId)) {
            groupState.set(itemId, defaultOpen)
        }
        return groupState.get(itemId)!
    }

    public cleanUp(groupId: string, itemId: string) {
        const groupState = this.getGroupState(groupId)
        groupState.delete(itemId)
    }

    public reset() {
        this.states.clear()
    }
}
const manager = new CollapsibleGroupManager()
export const collapsibleGroupManager = manager
// @ts-ignore
window.collapsibleGroupManager = manager
